'use client'

import React, { useCallback, useEffect, useMemo } from 'react'

import { ApolloNextAppProvider } from '@apollo/client-integration-nextjs'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import { DefaultToastOptions, Toaster } from 'react-hot-toast'
import { ThemeProvider } from 'styled-components'

import { Session } from '@actions/auth/types'
import { Authentication } from '@components/Authentication'
import { MUI_OPTIONS } from '@components/layout/providers/mui-options'
import { ThemeRegistry } from '@components/layout/providers/theme'
import { UserInfoProvider } from '@components/layout/providers/user-info'
import theme from '@components/layout/theme'
import { googleMapsApiKey } from '@constants/env'
import { makeClient } from '@graphql/ssr-client'
import Analytics from '@services/Analytics'
import { DebugServiceProvider } from '@services/debug/debug-service'
import { GoogleMapsProvider } from '@services/maps/google-maps'
import { useAuthSessionStore } from '@state/use-auth-session-store'

const TOAST_OPTIONS: DefaultToastOptions = {
  duration: 2000,
  style: {
    maxWidth: 'none',
  },
  error: {
    duration: 5000,
  },
}

type Props = {
  session: Session | null
  anonymousId?: string | null
  children: React.ReactNode
}
export default function HomeProviders({
  children,
  session,
  anonymousId,
}: Props) {
  useEffect(() => {
    // only set if we have a session
    // we are already removing session on logout
    if (!session) return
    useAuthSessionStore.setState({
      session,
    })
  }, [session])

  const client = useMemo(
    () => makeClient(session?.token, anonymousId),
    [session?.token, anonymousId]
  )
  const makeClientCallback = useCallback(() => client, [client])

  return (
    <ApolloNextAppProvider makeClient={makeClientCallback}>
      <ThemeRegistry options={MUI_OPTIONS}>
        <ThemeProvider theme={theme}>
          <GoogleMapsProvider apiKey={googleMapsApiKey}>
            <Analytics.Provider>
              <UserInfoProvider hasSession={!!session}>
                <DebugServiceProvider>
                  <Authentication>
                    <NuqsAdapter>{children}</NuqsAdapter>
                    <Toaster toastOptions={TOAST_OPTIONS} />
                  </Authentication>
                </DebugServiceProvider>
              </UserInfoProvider>
            </Analytics.Provider>
          </GoogleMapsProvider>
        </ThemeProvider>
      </ThemeRegistry>
    </ApolloNextAppProvider>
  )
}
